import { DEFAULT_PLAYER_VOLUME_FADE_DURATION, Player } from '@/shared/components/players/useAudioPlayer.ts';
import { IAudio } from '@/shared/components/types.ts';
import { MaybeNull } from '@/shared/types';
import { Capacitor } from '@capacitor/core';
import { defineStore, storeToRefs } from 'pinia';
import { ref, watch } from 'vue';
import { AppScreens } from '@/shared/types';
import { useMainStore } from '@/core/store/main.ts';

const getInitialAudioPreference = (key: string): boolean => {
  if (!Capacitor.isNativePlatform()) {
    return false;
  }
  const stored = localStorage.getItem(key);

  return stored === null ? true : JSON.parse(stored);
};

export const useBackgroundAudioPlayerStore = defineStore('backgroundAudioPlayerStore', () => {
  const soundEnabled = ref(getInitialAudioPreference('sound-enabled'));

  const backgroundAudio = ref<MaybeNull<IAudio>>(null);

  const player = ref<Player>();

  const getPlayer = (): Player => {
    if (!player.value) {
      throw new Error('Enable to use player. Player should be initialised using setPlayer function first');
    }

    return player.value;
  };

  const setPlayer = (p: Player) => {
    player.value = p;

    // On native, apply the initial sound state (watcher doesn't fire on init)
    if (soundEnabled.value) {
      p.unmute();
    }
  };

  watch(() => soundEnabled.value, (value: boolean) => {
    if (value) {
      localStorage.setItem('sound-enabled', 'true');
      getPlayer().unmute();
      getPlayer().play();
    } else {
      localStorage.setItem('sound-enabled', 'false');
      getPlayer().mute();
    }
  });

  // hide SoundtrackButton and disable (stop) background audio player
  const hideSoundBtn = ref(false);

  const { activeScreen } = storeToRefs(useMainStore());

  const sectionsWithSoundButton = [
    AppScreens.VideoBible,
    AppScreens.InteractiveBible,
    AppScreens.PrayerExperience,
    AppScreens.ReadingPlanDay,
    AppScreens.Story,
  ];

  const checkVisibleSoundButton = () => {
    if (sectionsWithSoundButton.includes(activeScreen.value)) {
      hideSoundBtn.value = false;
    } else {
      hideSoundBtn.value = true;
    }
  };

  watch(() => activeScreen.value, () => {
    checkVisibleSoundButton();
  });

  const isAudioScreen = (screen: AppScreens) => sectionsWithSoundButton.includes(screen);

  // The single place that stops the shared player when no audio-owning screen is
  // active. Screens own their own start/stop; this owns "no owner -> silence".
  watch(() => activeScreen.value, (screen) => {
    if (isAudioScreen(screen)) {
      return;
    }
    if (!player.value) {
      return;
    }
    const p = getPlayer();
    p.fadeOutVolume({
      duration: DEFAULT_PLAYER_VOLUME_FADE_DURATION,
    });
    setTimeout(() => {
      // Re-check: the user may have navigated back to an audio screen during the fade.
      if (!isAudioScreen(activeScreen.value)) {
        p.pause();
      }
    }, DEFAULT_PLAYER_VOLUME_FADE_DURATION);
  });

  checkVisibleSoundButton();

  return {
    soundEnabled,
    hideSoundBtn,
    backgroundAudio,
    setPlayer,
    getPlayer,
  };
});
